import React from "react";
import { useBrand } from "@/app/components/theme/brand-theme-boundary";
import { cn } from "@/lib/utils";

// Status types
export type TStatusTag =
  | "gebrauchtwagen"
  | "neuwagen"
  | "bestellfahrzeug"
  | "sofort-verfuegbar";

// Icon for used/new car (star)
const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 12 12"
    width={12}
    height={12}
    className={className}
  >
    <path
      fill="currentColor"
      d="m9.354375 7.6905.353775 4.0095-3.7081875-1.575075L2.2917825 11.7l.35373-4.0095L0 4.6544325l3.9267975-.9029625L6 0.3l2.0731875 3.4514525 3.926775.9029625-2.64555 3.0360675Z"
      strokeWidth={1}
    />
  </svg>
);

// Icon for "Bestellfahrzeug" (orange circle)
const OrangeCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <span
    className={
      `inline-block rounded-full bg-[#f09b00]` +
      (className ? ` ${className}` : "")
    }
    style={{ width: 9, height: 9 }}
  />
);

// Icon for "Sofort verfügbar" (green dot with fade blink animation)
const GreenDotIcon: React.FC<{ className?: string }> = ({ className }) => (
  <span
    className={
      `inline-block animate-[fade-blink_1.4s_ease-in-out_infinite] rounded-full bg-[#008300]` +
      (className ? ` ${className}` : "")
    }
    style={{ width: 9, height: 9 }}
  />
);

/**
 * StatusTag component
 *
 * Displays a colored tag with an icon and label to indicate the status of a vehicle offer.
 *
 * Supported statuses:
 * - "gebrauchtwagen": Used car (blue background, primary color, star icon)
 * - "neuwagen": New car (blue background, primary color, star icon)
 * - "bestellfahrzeug": Order vehicle (orange background, orange color, orange circle icon)
 * - "sofort-verfuegbar": Immediately available (green background, green color, green dot icon)
 *
 * Props:
 * @prop {TStatusTag} status - The status to display. Determines color, icon, and label.
 * @prop {string} [className] - Optional additional class names for the tag container.
 *
 * Example usage:
 * <StatusTag status="neuwagen" />
 */
const StatusTag: React.FC<{ status: TStatusTag; className?: string }> = ({
  status,
  className,
}) => {
  const { isAudi } = useBrand();
  // Config for each status
  const config = {
    gebrauchtwagen: {
      bg: "bg-[#f2f7fa]",
      text: "text-primary",
      icon: <StarIcon className="text-primary mr-1.5" />,
      label: "Gebrauchtwagen",
    },
    neuwagen: {
      bg: "bg-[#f2f7fa]",
      text: "text-primary",
      icon: <StarIcon className="text-primary mr-1.5" />,
      label: "Neuwagen",
    },
    bestellfahrzeug: {
      bg: "bg-[#ffe8bc99]",
      text: "text-[#f09b00]",
      icon: <OrangeCircleIcon className="mr-1.5" />,
      label: "Bestellfahrzeug",
    },
    "sofort-verfuegbar": {
      bg: "bg-[#cfffcd99]",
      text: "text-[#008300]",
      icon: <GreenDotIcon className="mr-1.5" />,
      label: "Sofort verfügbar",
    },
  } as const;

  const { bg, text, icon, label } = config[status];
  const isAudiSurfaceTag =
    isAudi &&
    (status === "gebrauchtwagen" ||
      status === "neuwagen" ||
      status === "bestellfahrzeug" ||
      status === "sofort-verfuegbar");

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-sm px-2 py-1 text-xs font-medium",
        bg,
        text,
        isAudiSurfaceTag &&
          "bg-primary text-primary-foreground rounded-md border-transparent",
        className
      )}
      data-testid="status-tag"
    >
      {React.cloneElement(icon, {
        className: cn(
          icon.props.className,
          isAudiSurfaceTag && status !== "sofort-verfuegbar" && "text-white",
          isAudiSurfaceTag &&
            (status === "sofort-verfuegbar" || status === "bestellfahrzeug") &&
            "bg-white"
        ),
      })}
      {label}
    </span>
  );
};

export default StatusTag;
